/**
 * Image Operations API Client
 *
 * Provides functions for aspect ratio changes and upscaling operations.
 * Supports both asset-based (existing images) and upload-based (new files) operations.
 */

import { apiClient } from '../api-client';

// ============================================
// Types
// ============================================

export interface AspectRatioParams {
  target_ratio: '1:1' | '3:2' | '4:3' | '16:9' | '2:3' | '9:16';
  target_width?: number;
  target_height?: number;
  crop_mode?: 'smart' | 'product' | 'person' | 'center';
  extend_background?: boolean;
  execution_mode?: 'auto' | 'sync' | 'async';
  save_as_new?: boolean;
}

export interface UpscaleParams {
  scale_factor?: number;
  model?: 'fsrcnn' | 'espcn' | 'lapsrn' | 'edsr' | 'lanczos';
  apply_sharpening?: boolean;
  execution_mode?: 'auto' | 'sync' | 'async';
  save_as_new?: boolean;
}

export interface OperationResult {
  mode: 'sync' | 'async';
  status: 'completed' | 'pending' | 'failed';
  asset_id?: number;
  task_id?: string;
  status_url?: string;
  error?: string;
  processing_time_ms?: number;
}

export interface TaskStatus {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  result_asset_id?: number;
  error?: string;
}

/**
 * Upload-based operation result (sync only)
 *
 * Note: Upload operations return image_id (UUID string),
 * while asset-based operations return asset_id (number)
 */
export interface UploadOperationResult {
  success: boolean;
  image_id?: string;  // For upload-based operations (UUID)
  asset_id?: number;  // For asset-based operations
  image_url: string;
  original_dimensions?: { width: number; height: number };
  new_dimensions?: { width: number; height: number };
  scale_factor?: number;
  model?: string;
  processing_time_ms?: number;
  message?: string;
  error?: string;
}

// ============================================
// Asset-based Operations (existing images)
// ============================================

/**
 * Change aspect ratio of an asset
 */
export async function changeAspectRatio(
  assetId: number,
  params: AspectRatioParams
): Promise<OperationResult> {
  const response = await apiClient.post<OperationResult>(
    `/api/v1/assets/${assetId}/change-aspect-ratio`,
    params
  );
  return response.data;
}

/**
 * Upscale an asset
 */
export async function upscaleImage(
  assetId: number,
  params: UpscaleParams
): Promise<OperationResult> {
  const response = await apiClient.post<OperationResult>(
    `/api/v1/assets/${assetId}/upscale`,
    params
  );
  return response.data;
}

/**
 * Get task status for async operations
 */
export async function getTaskStatus(taskId: string): Promise<TaskStatus> {
  const response = await apiClient.get<TaskStatus>(`/api/v1/tasks/${taskId}`);
  return response.data;
}

/**
 * Poll task status until completion
 *
 * @param taskId - Task ID to poll
 * @param onProgress - Callback for progress updates
 * @param pollInterval - Polling interval in ms (default: 2000)
 * @returns Final task status
 */
export async function pollTaskStatus(
  taskId: string,
  onProgress?: (status: TaskStatus) => void,
  pollInterval: number = 2000
): Promise<TaskStatus> {
  return new Promise((resolve, reject) => {
    const interval = setInterval(async () => {
      try {
        const status = await getTaskStatus(taskId);

        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'completed') {
          clearInterval(interval);
          resolve(status);
        } else if (status.status === 'failed') {
          clearInterval(interval);
          reject(new Error(status.error || 'Task failed'));
        }
      } catch (error) {
        clearInterval(interval);
        reject(error);
      }
    }, pollInterval);
  });
}

// ============================================
// Upload-based Operations (new files)
// ============================================

/**
 * Change aspect ratio of an uploaded file
 *
 * @param file - Image file to process
 * @param params - AspectRatio parameters
 * @returns Promise with new asset details
 */
export async function changeAspectRatioUpload(
  file: File,
  params: Omit<AspectRatioParams, 'execution_mode' | 'save_as_new'>
): Promise<UploadOperationResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('target_ratio', params.target_ratio);

  if (params.crop_mode) {
    formData.append('crop_mode', params.crop_mode);
  }

  if (params.extend_background !== undefined) {
    formData.append('extend_background', String(params.extend_background));
  }

  if (params.target_width !== undefined) {
    formData.append('target_width', String(params.target_width));
  }

  if (params.target_height !== undefined) {
    formData.append('target_height', String(params.target_height));
  }

  const response = await apiClient.post<UploadOperationResult>(
    '/api/v1/image-operations/change-aspect-ratio',
    formData
  );
  return response.data;
}

/**
 * Upscale an uploaded file
 *
 * @param file - Image file to process
 * @param params - Upscale parameters
 * @returns Promise with new asset details
 */
export async function upscaleImageUpload(
  file: File,
  params: Omit<UpscaleParams, 'execution_mode' | 'save_as_new'>
): Promise<UploadOperationResult> {
  const formData = new FormData();
  formData.append('file', file);

  if (params.scale_factor !== undefined) {
    formData.append('scale_factor', String(params.scale_factor));
  }

  if (params.model) {
    formData.append('model', params.model);
  }

  if (params.apply_sharpening !== undefined) {
    formData.append('apply_sharpening', String(params.apply_sharpening));
  }

  const response = await apiClient.post<UploadOperationResult>(
    '/api/v1/image-operations/upscale',
    formData
  );
  return response.data;
}
